import itemParser from './parsers/item';
import toError from './parsers/error';
import triggerProgressWhileTranscoding from './trigger-progress-while-transcoding';

import * as http from '../../utils/http';
import { keysToCamelCase, keysToSnakeCase } from '@wix/wix-vod-shared/src/common/utils/object';


// TODO: switch to whitelist instead of blacklist
export const excludedFields = [
  'updateUrl', 'listId', 'qualities', 'mediaInfo',
  'createdTs', 'updatedTs', 'usedInLists', 'getVideoUrls', 'getVideoUrlsByFileName',
  'transcodingStatus', 'trailerTranscodingStatus', 'uploadingStatus', 'trailerInfo', 'trailerStatus',
  'id', 'channelId', 'channelData', 'usedCount', 'isNew', 'error', 'errorInfo',
  'currentFileProgress', 'uploadedSize', 'fileSize', 'listsRefs',
];

function _trimAndUniq(array) {
  return _(array)
    .map(_.trim)
    .uniq()
    .value();
}


export function prepareItemForSending(itemData) {
  itemData = keysToCamelCase(itemData);

  const { publishTs, categories, tags, itemType } = itemData;

  if (publishTs) {
    itemData.publishTs = Math.round(publishTs.valueOf() / 1000);
  }

  if (categories) {
    itemData.categories = _trimAndUniq(categories);
  }
  if (tags) {
    itemData.tags = _trimAndUniq(tags);
  }
  if (!itemType) {
    itemData = _.omit(itemData, ['itemType']);
  }

  return keysToSnakeCase(_.omit(itemData, excludedFields));
}


export default function (settings, adapterMethods) {

  const toItem = _.partial(itemParser, adapterMethods);

  function _parseSuccessResponse(response) {
    let { data } = response;

    if (_.isArray(data)) {
      data = data[0];
    }

    return {
      ...response,
      data: toItem(data),
    };
  }

  function _connectItemToList(trigger, itemResponse, listId) {
    if (!listId || _.find(itemResponse.data.usedInLists, { itemId: listId })) {
      return Promise.resolve(itemResponse);
    }

    return adapterMethods.list.get(trigger, listId)
      .then(_.property('data.title'))
      .then(listTitle => adapterMethods.listItems.add(trigger, listId, itemResponse.data.id)
        .then(function () {
          itemResponse.data.usedInLists = [{
            id: listId,
            title: listTitle,
          }];
        }),
      )
      .then(() => adapterMethods.list.publish(trigger, listId));
  }

  function _tryToCreateInList(trigger, itemData) {
    const {
      file,
      title,
      listId,
      mediaType = settings.mediaType,
    } = itemData;

    const creationData = {
      title,
      mediaType,
      listId,
    };

    return adapterMethods.file.canUploadVideo(trigger, file)
      .then(() => adapterMethods.item.createInList(trigger, creationData, listId))
      .catch(toError);
  }

  function get(trigger, itemId) {
    if (!itemId) {
      return toError({
        status: 500,
        data: {
          errorCode: 500,
          errorDescription: 'no id provided',
        },
      });
    }

    return http.get(`${settings.apiUrl}/catalog/items/${itemId}`)
      .then(_parseSuccessResponse)
      .then(function (parsedResponse) {
        triggerProgressWhileTranscoding([parsedResponse.data]);
        return parsedResponse;
      })
      .catch(toError);
  }

  function create(trigger, itemData) {
    return adapterMethods.items.create(trigger, [itemData])
      .then(response => ({
        ...response,
        data: response.data[0],
      }));
  }

  function createInList(trigger, itemData, listId) {
    return adapterMethods.item.create(trigger, itemData)
      .then(function (itemResponse) {
        return _connectItemToList(trigger, itemResponse, listId)
          .then(() => itemResponse)
          .catch(() => itemResponse);
      })
      .catch(toError);
  }

  function update(trigger, itemData) {
    const { id } = itemData;
    const data = prepareItemForSending(itemData);

    return http.put(`${settings.apiUrl}/catalog/items/${id}`, { data })
      .then(_parseSuccessResponse)
      .catch(toError);
  }

  function upload(trigger, itemData) {
    const { file, mediaType = settings.mediaType } = itemData;

    return _tryToCreateInList(trigger, itemData)
      .then(itemResponse => {
        const catalogCallbackUrl = itemResponse.data.updateUrl;
        return adapterMethods.file.uploadVideo(trigger, { catalogCallbackUrl, file, mediaType })
          .then(() => itemResponse);
      })
      .then(itemResponse => adapterMethods.item.get(trigger, itemResponse.data.id));
  }

  function uploadByUrl(trigger, itemData) {
    const { file, mediaType = settings.mediaType } = itemData;

    return _tryToCreateInList(trigger, itemData)
      .then(itemResponse => {
        const catalogCallbackUrl = itemResponse.data.updateUrl;
        const uploadFn = adapterMethods.file.uploadByUrl;
        const fileProps = _.omit(file, 'duration');

        return adapterMethods.file
          .uploadVideo(trigger, { catalogCallbackUrl, mediaType, ...fileProps }, uploadFn)
          .then(videoFile => ({ videoFile, itemResponse }));
      });
  }

  function reupload(trigger, uploadData) {
    const {
      file,
      itemId,
      listId,
      mediaType = settings.mediaType,
      updateUrl: catalogCallbackUrl,
    } = uploadData;

    return adapterMethods.file.canUploadVideo(trigger, file)
      .then(function () {
        return adapterMethods.item.get(trigger, itemId);
      })
      .then(function (itemResponse) {
        // fields can be changed while lists requests in progress
        itemResponse.data = _.omit(itemResponse.data, ['transcodingStatus', 'uploadingStatus']);
        return _connectItemToList(trigger, itemResponse, listId)
          .then(() => itemResponse)
          .catch(() => itemResponse);
      })
      .then(() => adapterMethods.file.uploadVideo(trigger, { catalogCallbackUrl, file, mediaType }))
      .then(function (fileResponse) {
        return adapterMethods.item.update(trigger, {
          id: itemId,
          mediaFileName: fileResponse.data.id,
        });
      })
      .catch(toError);

  }

  function getVideoUrls(trigger, itemId, instance) {
    const params = {
      instance,
    };

    return http.get(`${settings.apiUrl}/public/play/${itemId}`, { params })
      .then(_.property('data'))
      .catch(toError);
  }


  function getVideoUrlsByFileName(trigger, fileName, params = {}) {
    if (!fileName) {
      return Promise.reject();
    }

    return http.get(`${settings.apiUrl}/catalog/videos/${fileName}/play`, { params })
      .then(_.property('data'))
      .catch(toError);
  }

  return {
    get,
    create,
    createInList,
    update,
    upload,
    reupload,
    uploadByUrl,
    getVideoUrls,
    getVideoUrlsByFileName,
  };
}



// WEBPACK FOOTER //
// ./src/transport/adapters/vod/item.js